import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { supabase } from './supabase';
import { handleError } from '../utils/errorHandling';

// Define the subscription plan structure
interface SubscriptionPlanInfo {
  name: string;
  maxProjects: number | 'unlimited';
  planId?: string; // Optional planId for determining billing interval
}

const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

if (!STRIPE_PUBLIC_KEY) {
  throw new Error('Missing Stripe public key');
}

let stripePromise: Promise<Stripe | null>;

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
  }
  return stripePromise;
};

// Check if user is an admin
export const isUserAdmin = async () => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return false;

    // Fetch user's admin status
    const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });

    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

export const createCheckoutSession = async (priceId: string) => {
  try {
    console.log('Creating checkout session for price ID:', priceId);

    // Get the current user's session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('No active session');
    }

    console.log('User session found:', session.user?.id);

    // Call Supabase Function to create checkout session
    console.log('Invoking Supabase Function: create-checkout-session');
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: JSON.stringify({ priceId }),
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    console.log('Supabase Function Response:', { data, error });

    if (error) {
      throw error;
    }

    // Redirect to Stripe Checkout
    if (data?.session?.url) {
      console.log('Redirecting to Stripe Checkout:', data.session.url);
      window.location.href = data.session.url;
    } else {
      throw new Error('No checkout URL received');
    }
  } catch (error: unknown) {
    handleError('Creating checkout session', error, {
      showNotification: true,
      notificationPrefix: 'Subscription error',
    });

    // Re-throw the error so calling code can handle it if needed
    throw error;
  }
};

export const createPortalSession = async () => {
  try {
    // Get the current user's session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('No active session');
    }

    // Call Supabase Function to create portal session
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;

    // Redirect to Stripe Customer Portal
    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No portal session URL received');
    }
  } catch (error: unknown) {
    handleError('Creating customer portal session', error, {
      showNotification: true,
      notificationPrefix: 'Subscription management error',
    });
    throw error;
  }
};

interface SubscriptionStatusResult {
  isSubscribed: boolean;
  currentPlan: SubscriptionPlanInfo;
  subscriptionStatus: string;
}

export const checkSubscriptionStatus = async (
  userId: string
): Promise<SubscriptionStatusResult> => {
  try {
    // Get the current user's session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('No active session');
    }

    // Call Supabase Function to check subscription status
    const { data, error } = await supabase.functions.invoke('check-subscription-status', {
      body: JSON.stringify({ userId }),
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;

    if (!data) {
      return {
        isSubscribed: false,
        currentPlan: {
          name: 'Free',
          maxProjects: 3,
        } as SubscriptionPlanInfo,
        subscriptionStatus: 'Inactive',
      };
    }

    // Add special handling for VIP plan
    const isVip = data.currentPlan?.name === 'VIP';

    return {
      isSubscribed: data.isSubscribed || isVip,
      currentPlan: isVip
        ? ({
            name: 'VIP',
            maxProjects: 'unlimited',
          } as SubscriptionPlanInfo)
        : (data.currentPlan as SubscriptionPlanInfo),
      subscriptionStatus: isVip ? 'active' : data.subscriptionStatus,
    };
  } catch (error: unknown) {
    handleError('Checking subscription status', error, {
      // Not showing alert here as this is a background operation
      // that shouldn't disrupt the user experience
    });

    // Return a default state that considers the user not subscribed
    return {
      isSubscribed: false,
      currentPlan: {
        name: 'Free',
        maxProjects: 3,
      } as SubscriptionPlanInfo,
      subscriptionStatus: 'Inactive',
    };
  }
};
